import {
  ArrowRight01Icon,
  Building03Icon,
  CheckmarkBadge02Icon,
  SquareLock02Icon,
  UnavailableIcon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useSelectCandidateContext } from 'hooks/use-select-candidate-context';
import { Tab } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Moment from 'react-moment';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Badge } from 'components/Badge/Badge';
import { IconComponent } from 'components/Icon/IconComponent';
import { Loading } from 'components/Loading/Loading';
import SliderMotion from 'components/Motion/SliderMotion';
import StatusPill from 'components/Pills/StatusPills';
import { OverflowTabs } from 'components/Tabs/Tabs';

import { selectLinkedCandidates, selectPortalAuth } from 'api/candidate-portal-slice';

import CandidatePortalPage from './CandidatePortalPage/CandidatePortalPage';

const CandidatePortalContextSelector = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isAuthenticated } = useSelector(selectPortalAuth);
  const linkedCandidates = useSelector(selectLinkedCandidates);

  const { handleSelectCandidate, isLoading } = useSelectCandidateContext();

  const formatStatus = status => {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'in-progress':
        return 'In Progress';
      case 'candidate':
        return 'Pending';
      case 'overdue':
        return 'Overdue';
      default:
        return status;
    }
  };

  const isCandidateActive = candidate => {
    return !candidate.canceled && !candidate.deleted;
  };

  const activeCandidates = linkedCandidates?.filter(candidate => isCandidateActive(candidate));
  const inactiveCandidates = linkedCandidates?.filter(candidate => !isCandidateActive(candidate));

  const renderCandidateCard = (candidate, status = true) => {
    const active = isCandidateActive(candidate);
    const handleKeyDown = e => {
      if ((e.key === 'Enter' || e.key === ' ') && active) {
        e.preventDefault();
        handleSelectCandidate(candidate);
      }
    };

    return (
      <div
        key={candidate.link_id}
        role={active ? 'button' : 'article'}
        tabIndex={active ? 0 : -1}
        aria-label={
          active
            ? t('candidatePortal.selectApplicationForCompany', { company: candidate.company_name })
            : t('candidatePortal.inactiveApplication', { company: candidate.company_name })
        }
        aria-disabled={!active}
        className={`group w-full flex-1 flex align-items-center justify-between p-8 gap-4 bg-white hover:bg-primary-1 border hover:border-brand-primary hover:border-2 border-border-primary rounded-xl transition-all duration-200 text-left rounded-2xl bg-white shadow-sm shadow-soft focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-text-primary ${active ? 'cursor-pointer' : 'cursor-not-allowed pointer-events-none'}`}
        onClick={() => {
          active ? handleSelectCandidate(candidate) : null;
        }}
        onKeyDown={handleKeyDown}>
        <div className="flex gap-8 items-center w-full">
          {candidate.brand_logo_url ? (
            <div className="w-24 h-24 flex items-center justify-center rounded-full border border-border-primary bg-white">
              <img
                src={candidate.brand_logo_url}
                alt={t('candidatePortal.companyLogo', { company: candidate.company_name })}
                className="max-w-full max-h-full object-contain rounded-full p-2"
              />
            </div>
          ) : (
            <IconComponent
              icon={Building03Icon}
              size="xlarge"
              strokeWidth={1.5}
              className={active ? '' : 'opacity-50'}
              aria-hidden="true"
            />
          )}
          <div className="flex flex-col sm:flex-row sm:items-center sm:gap-8 gap-4 sm:justify-between justify-start flex-1">
            <div className="flex flex-col flex-1">
              <h3 className="text-3xl text-text-primary font-regular m-0">
                {candidate.company_name}
              </h3>
              <div className="flex sm:gap-2 gap-1 sm:items-center sm:flex-row flex-col">
                {candidate.job_role && (
                  <div className="flex items-center gap-2">
                    <span className="text-text-tertiary font-light text-xl">
                      {candidate.job_role}
                    </span>
                    <span
                      className="invisible sm:visible text-text-secondary font-light text-xl"
                      aria-hidden="true">
                      •
                    </span>
                  </div>
                )}
                <time
                  className="text-text-tertiary font-light text-xl"
                  dateTime={candidate.link_created_at}>
                  <Moment fromNow>{candidate.link_created_at}</Moment>
                </time>
              </div>
            </div>
            <span
              aria-label={t('candidatePortal.applicationStatus', {
                status: status
                  ? formatStatus(candidate.candidate_status)
                  : t('candidatePortal.locked'),
              })}>
              <StatusPill
                className="px-4 text-capitalize"
                status={status ? candidate.candidate_status : 'warning'}
                text={
                  status ? formatStatus(candidate.candidate_status) : t('candidatePortal.locked')
                }
              />
            </span>
          </div>
        </div>
        <div className="flex items-center gap-8 sm:justify-end justify-between" aria-hidden="true">
          <HugeiconsIcon
            icon={active ? ArrowRight01Icon : SquareLock02Icon}
            className="text-text-tertiary group-hover:text-brand-primary"
            aria-hidden="true"
          />
        </div>
      </div>
    );
  };

  const renderCandidates = (candidates, status = true) => {
    return (candidates || [])?.map(candidate => renderCandidateCard(candidate, status));
  };

  return (
    <CandidatePortalPage candidate={linkedCandidates[0]} showFooter={false}>
      <Loading active={isLoading} />

      <div className="flex-1 flex items-center justify-center min-h-[calc(100vh-7rem)]">
        <div className="w-full max-w-6xl">
          <div className="border-0 p-12 space-y-12 flex flex-col align-items-center">
            <header className="flex flex-col align-items-center space-y-2 max-w-2xl">
              <h1 className="text-5xl font-medium text-text-primary m-0">
                {t('candidatePortal.selectAnApplication')}
              </h1>
              <p className="text-text-secondary font-light text-center m-0">
                {t('candidatePortal.multipleApplicationsAssociatedWithYourAccount')}
              </p>
            </header>

            <div className="w-full flex flex-col gap-8 justify-center">
              <SliderMotion
                key={`context-selector-${linkedCandidates?.length}-checks`}
                duration={0.5}
                delay={0.4}
                className="tab-container">
                <OverflowTabs
                  defaultActiveKey="active"
                  className="mx-auto"
                  mountOnEnter={true}
                  unmountOnExit={true}>
                  <Tab
                    eventKey="active"
                    title={
                      <span className="flex items-center gap-2">
                        <HugeiconsIcon
                          icon={CheckmarkBadge02Icon}
                          size="16"
                          strokeWidth={2}
                          aria-hidden="true"
                        />{' '}
                        {t('candidatePortal.active')}{' '}
                        {activeCandidates.length > 0 ? (
                          <Badge
                            variant="success"
                            text={activeCandidates.length}
                            aria-label={t('candidatePortal.activeApplicationsCount', {
                              count: activeCandidates.length,
                            })}
                          />
                        ) : (
                          ''
                        )}
                      </span>
                    }>
                    <div
                      className="p-4 flex flex-col gap-4"
                      role="list"
                      aria-label={t('candidatePortal.activeApplications')}>
                      {renderCandidates(activeCandidates)}
                    </div>
                  </Tab>
                  <Tab
                    eventKey="inactive"
                    title={
                      <span className="flex items-center gap-2">
                        <HugeiconsIcon
                          icon={UnavailableIcon}
                          size="16"
                          strokeWidth={2}
                          aria-hidden="true"
                        />{' '}
                        {t('candidatePortal.inactive')}{' '}
                        {inactiveCandidates.length > 0 ? (
                          <Badge
                            variant="warning"
                            text={inactiveCandidates.length}
                            aria-label={t('candidatePortal.inactiveApplicationsCount', {
                              count: inactiveCandidates.length,
                            })}
                          />
                        ) : (
                          ''
                        )}
                      </span>
                    }>
                    <div className="p-4 flex flex-col gap-4">
                      <p className="text-text-secondary font-light text-center m-0">
                        {t('candidatePortal.inActiveDescription')}{' '}
                        <a
                          href={`mailto:${process.env.REACT_APP_SUPPORT_EMAIL}`}
                          className="text-primary-4 hover:text-primary-5 font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-text-primary rounded"
                          aria-label={t('candidatePortal.contactSupportViaEmail', {
                            email: process.env.REACT_APP_SUPPORT_EMAIL,
                          })}>
                          {process.env.REACT_APP_SUPPORT_EMAIL}
                        </a>
                        .
                      </p>
                    </div>
                    <div
                      className="p-4 flex flex-col gap-4"
                      role="list"
                      aria-label={t('candidatePortal.inactiveApplications')}>
                      {renderCandidates(inactiveCandidates, false)}
                    </div>
                  </Tab>
                </OverflowTabs>
              </SliderMotion>
            </div>
          </div>
        </div>
      </div>
    </CandidatePortalPage>
  );
};

export default CandidatePortalContextSelector;
